import { dateCompare, isNullOrWhitespace, strCompareLowerCase } from '@common/Common';
import WorkspaceNavigation from '@common/Components/WorkspaceNavigation.vue';
import { ICommandStatus } from "@cqrs/cqrs";
import WorkspacesQueries from "@Organizations/Workspaces/queries-vue-component";
import { UserWorkspacesEntriesReadModel } from '@Organizations/Workspaces/readmodels';
import { DecisionProjectBuilderViewModel } from "@Projects/Components/DecisionProjectBuilder";
import DecisionProjectBuilder from '@Projects/Components/DecisionProjectBuilder.vue';
import { PortfolioProjectBuilderViewModel } from "@Projects/Components/PortfolioProjectBuilder";
import PortfolioProjectBuilder from '@Projects/Components/PortfolioProjectBuilder.vue';
import { ProjectClonerViewModel } from "@Projects/Components/ProjectCloner";
import ProjectCloner from '@Projects/Components/ProjectCloner.vue';
import { ProjectEditorViewModel } from "@Projects/Components/ProjectEditor";
import ProjectEditor from '@Projects/Components/ProjectEditor.vue';
import { ProjectTemplateLinksManagerViewModel } from "@Projects/Components/ProjectTemplateLinksManager";
import ProjectTemplateLinksManager from '@Projects/Components/ProjectTemplateLinksManager.vue';
import { ProjectWorkspaceSwitcherViewModel } from "@Projects/Components/ProjectWorkspaceSwitcher";
import ProjectWorkspaceSwitcher from '@Projects/Components/ProjectWorkspaceSwitcher.vue';
import { CloneProjectCommandV1, CreateDecisionProjectCommandV1, CreatePortfolioProjectCommandV1, DeleteProjectCommandV1, ExportProjectCommandV1, SwitchProjectWorkspaceCommandV1, UpdateProjectCommandV1, UpdateProjectTemplateLinksCommandV1 } from '@Projects/Domain/commands-core';
import ProjectCommands from "@Projects/Domain/commands-vue-component";
import ProjectQueries from "@Projects/Domain/queries-vue-component";
import { ProjectReadModel, ProjectsPageReadModel, ProjectTemplateLinksReadModel } from "@Projects/Domain/readmodels";
import { ProjectTemplateLink } from "@Projects/Model";
import Cookies from "js-cookie";
import { Component, mixins } from 'vue-facing-decorator';


@Component({
    components: { ProjectEditor, ProjectCloner, ProjectWorkspaceSwitcher, ProjectTemplateLinksManager, PortfolioProjectBuilder, DecisionProjectBuilder, WorkspaceNavigation }
})
export default class ProjectsPage extends mixins(ProjectQueries, ProjectCommands, WorkspacesQueries)
{
    get workspaceId(): number { return Number(this.$route?.params?.workspaceId ?? 0); }

    initialized: boolean = false;
    workspaceName: string = null;
    searchPhrase: string = null;
    orderBy: string = null;
    orderByDescending: boolean = false;
    projects: ProjectReadModel[] = [];

    portfolioProjectBuilder = new PortfolioProjectBuilderViewModel();
    decisionProjectBuilder = new DecisionProjectBuilderViewModel();
    projectEditor = new ProjectEditorViewModel();
    projectCloner = new ProjectClonerViewModel();
    projectWorkspaceSwitcher: ProjectWorkspaceSwitcherViewModel | null = null;    
    projectTemplateLinksManager = new ProjectTemplateLinksManagerViewModel();

    get projectsView(): ProjectReadModel[]
    {
        let view = new Array(...this.projects);

        if (!isNullOrWhitespace(this.searchPhrase))
        {
            const phrase = this.searchPhrase.toLowerCase();
            view = view.filter(o => o.Name.toLowerCase().indexOf(phrase) >= 0);
        }

        if (this.orderBy === "name")
        {
            view = view.sort((a, b) => strCompareLowerCase(a.Name, b.Name));
        }
        else if (this.orderBy === "created")
        {
            view = view.sort((a, b) => dateCompare(a.Created, b.Created));
        }
        else if (this.orderBy === "last activity")
        {
            view = view.sort((a, b) => dateCompare(a.LastActivity, b.LastActivity));
        }

        if (this.orderByDescending === true)
        {
            view = view.reverse();
        }

        return view;
    }

    initializeViewSettings()
    {
        const settings = Cookies.get("ViewSettings_Projects");

        if (settings)
        {
            const jsonSettings = JSON.parse(settings);
            const allowedOrderBy = ["name", "application", "created", "last activity"];
            const orderBy = allowedOrderBy.find(a => a === jsonSettings.OrderBy) || "name";
            this.searchPhrase = null;
            this.orderBy = orderBy;
            this.orderByDescending = jsonSettings.OrderByDescending || false;
        }
        else
        {
            this.searchPhrase = null;
            this.orderBy = "name";
            this.orderByDescending = false;
        }
    }

    persistViewSettings()
    {
        const settings = { OrderBy: this.orderBy, OrderByDescending: this.orderByDescending };
        const expiration = new Date();
        expiration.setDate(expiration.getDate() + 999);

        Cookies.set("ViewSettings_Projects", JSON.stringify(settings), { expires: expiration, path: "/" });
    }


    orderByName()
    {
        if (this.orderBy === "name")
        {
            this.orderByDescending = !this.orderByDescending;
        }
        else
        {
            this.orderBy = "name";
            this.orderByDescending = false;
        }

        this.persistViewSettings();
    }

    orderByCreated()
    {

        if (this.orderBy === "created")
        {
            this.orderByDescending = !this.orderByDescending;
        }
        else
        {
            this.orderBy = "created";
            this.orderByDescending = false;
        }

        this.persistViewSettings();
    }

    orderByLastActivity()
    {
        if (this.orderBy === "last activity")
        {
            this.orderByDescending = !this.orderByDescending;
        }
        else
        {
            this.orderBy = "last activity";
            this.orderByDescending = false;
        }

        this.persistViewSettings();
    }


    showPortfolioBuilder()
    {
        this.portfolioProjectBuilder.show();
    }

    showDecisionBuilder()
    {
        this.decisionProjectBuilder.show();
    }

    showProjectEditor(p: ProjectReadModel)
    {
        this.projectEditor.show(p.ProjectId, p.Name, p.Description);
    }

    showProjectCloner(p: ProjectReadModel)
    {
        this.projectCloner.show(p.ProjectId, p.Name);
    }

    showProjectTemplateLinksManager(p: ProjectReadModel)
    {
        this.projectTemplateLinksManager.show(p.ProjectId, p.Name);
        this.runProjectTemplateLinksQueryV1(this.workspaceId, p.ProjectId);
    }

    showProjectWorkspaceSwitcher(p: ProjectReadModel)
    {
        this.projectWorkspaceSwitcher.show(p.ProjectId, p.Name);

        if (!this.projectWorkspaceSwitcher.initialized)
        {
            this.runAuthUserWorkspacesEntriesQueryV1();
        }
    }

    exportToJson(p: ProjectReadModel)
    {
        this.invokeExportProjectCommandV1(p.ProjectId.toString())
    }


    handleCreatePortfolioProjectCommand(name: string, description: string | null)
    {
        this.invokeCreatePortfolioProjectCommandV1(this.workspaceId, name, description);
    }

    handleCloneProjectCommand(sourceId: number, cloneName: string)
    {
        this.invokeCloneProjectCommandV1(this.workspaceId, sourceId, cloneName);
    }

    handleCreateDecisionProjectCommand(name: string, description: string | null)
    {
        this.invokeCreateDecisionProjectCommandV1(this.workspaceId, name, description);
    }

    handleSwitchProjectWorkspaceCommand(projectId: number, targetWorkspaceId: number)
    {
        if (this.workspaceId !== targetWorkspaceId)
        {
            this.invokeSwitchProjectWorkspaceCommandV1(this.workspaceId, projectId, targetWorkspaceId);
        }
    }

    handleUpdateProjectTemplateLinksCommand(projectId: number, links: ProjectTemplateLink[])
    {
        this.invokeUpdateProjectTemplateLinksCommandV1(this.workspaceId, projectId, links);
    }

    handleUpdateProjectCommand(projectId: number, name: string, description: string | null)
    {
        this.invokeUpdateProjectCommandV1(this.workspaceId, projectId, name, description);
    }

    handleDeleteProjectCommand(projectId: number)
    {
        this.invokeDeleteProjectCommandV1(this.workspaceId, projectId);
    }

    mounted()
    {
        document.title = "Projects | TransparentChoice";
        this.projectWorkspaceSwitcher = new ProjectWorkspaceSwitcherViewModel(this.workspaceId);
        this.runProjectsPageQueryV1(this.workspaceId);
    }

    onSetModel(model: any, modelName: string)
    {
        if (modelName === "ProjectsPageReadModel")
        {
            const rm = model as ProjectsPageReadModel;
            this.workspaceName = rm.WorkspaceName;
            this.projects = rm.Projects;
            this.initializeViewSettings();
            this.initialized = true;
        }
        else if (modelName === "ProjectReadModel")
        {
            const rm = model as ProjectReadModel;

            const curr = this.projects.find(a => a.ProjectId === rm.ProjectId);

            if (curr)
            {
                this.projects.remove(curr);
            }

            this.projects.push(rm);
        }
        else if (modelName === "UserWorkspacesEntriesReadModel")
        {
            const rm = model as UserWorkspacesEntriesReadModel;
            this.projectWorkspaceSwitcher.setData(rm.Workspaces);
        }
        else if (modelName === "ProjectTemplateLinksReadModel")
        {
            const rm = model as ProjectTemplateLinksReadModel;
            if (rm.ProjectId === this.projectTemplateLinksManager.projectId)
            {
                this.projectTemplateLinksManager.setData(rm);
            }
        }
    }

    onCommandSucceed(cmd: any, correlationId: string, result: any)
    {
        if (UpdateProjectCommandV1.matchType(cmd))
        {
            const projectId = parseInt(correlationId);
            this.runProjectByIdQueryV1(this.workspaceId, projectId);

            this.projectEditor.close();
        }
        else if (DeleteProjectCommandV1.matchType(cmd))
        {
            const projectId = Number.parseInt(correlationId);
            this.projects = this.projects.filter(a => a.ProjectId !== projectId);

            this.projectEditor.close();
        }
        else if (CloneProjectCommandV1.matchType(cmd))
        {
            this.projectCloner.close();

            const projectId = Number.parseInt(result);
            this.$router.push({ path: `/project/${projectId}` });
        }
        else if (SwitchProjectWorkspaceCommandV1.matchType(cmd))
        {
            this.projectWorkspaceSwitcher.close();

            const projectId = Number.parseInt(correlationId);
            this.projects = this.projects.filter(a => a.ProjectId !== projectId);
        }
        else if (CreatePortfolioProjectCommandV1.matchType(cmd))
        {
            this.portfolioProjectBuilder.close();

            const projectId = Number.parseInt(result);
            this.$router.push({ path: `/project/${projectId}` });
        }
        else if (CreateDecisionProjectCommandV1.matchType(cmd))
        {
            this.decisionProjectBuilder.close();

            const projectId = Number.parseInt(result);
            this.$router.push({ path: `/project/${projectId}` });
        }
        else if (UpdateProjectTemplateLinksCommandV1.matchType(cmd))
        {
            const projectId = Number.parseInt(correlationId);
            this.projectTemplateLinksManager.resetData();
            this.runProjectTemplateLinksQueryV1(this.workspaceId, projectId);
        }
        else if (ExportProjectCommandV1.matchType(cmd))
        {
            if (result)
            {
                location.href = result;
            }
        }
    }

    onCommandFailed(status: ICommandStatus)
    {
        if (CloneProjectCommandV1.matchType(status.Command))
        {
            this.projectCloner.displayValidationErrors(status.ValidationErrors);
        }
        else if (CreatePortfolioProjectCommandV1.matchType(status.Command))
        {
            this.portfolioProjectBuilder.displayValidationErrors(status.ValidationErrors);
        }
        else if (CreateDecisionProjectCommandV1.matchType(status.Command))
        {
            this.decisionProjectBuilder.displayValidationErrors(status.ValidationErrors);
        }
        else if (UpdateProjectTemplateLinksCommandV1.matchType(status.Command))
        {
            this.projectTemplateLinksManager.displayValidationErrors(status.ValidationErrors);
        }
        else if (UpdateProjectCommandV1.matchType(status.Command))
        {
            this.projectEditor.displayValidationErrors(status.ValidationErrors);
        }
        else if (DeleteProjectCommandV1.matchType(status.Command))
        {
            this.projectEditor.displayValidationErrors(status.ValidationErrors);
        }
    }
}